/* globals svgEditor, seAlert */
import { insertImageFiles, insertImageFromDataUrl, fetchImageForEmbed, blobToDataURL } from './utils/insertImageService.js'

/**
 * Insert-image & sticker controller.
 *
 * Single entry point for all raster image sources:
 *  - "Insert image" in the main menu (tool_insert_image)
 *  - the image tool button in the left panel (tool_image): opens the dialog
 *    instead of the old native prompt()
 *  - Ctrl/Cmd+V with an image on the system clipboard
 *
 * It also owns the sticker cut-line dialog (se-sticker-dialog): the cut path
 * is added as a real SVG <path> above the image, so it is saved with the
 * artifact and can be exported for a cutting plotter.
 *
 * Every insertion goes through insertImageService, so images are embedded as
 * data URIs and the artifact autosave fires via the canvas `changed` event.
 */

/**
 * Opens the insert-image dialog from any entry point.
 * @returns {void}
 */
export const openInsertImageDialog = () => {
  document.getElementById('se-insert-image-dialog')?.setAttribute('dialog', 'open')
}

/**
 * Opens the sticker cut-line dialog for the given source.
 * @param {import('./dialogs/stickerDialog.js').StickerSource} [source]
 * @returns {void}
 */
export const openStickerDialog = (source) => {
  const dialog = document.getElementById('se-sticker-dialog')
  if (!dialog) {
    console.error('insert-image: sticker dialog element missing')
    return
  }
  if (source) dialog.setAttribute('source', source)
  dialog.setAttribute('dialog', 'open')
}

/**
 * @param {File[]} files
 * @returns {Promise<void>}
 */
const insertFilesSafely = async (files) => {
  try {
    await insertImageFiles(files)
  } catch (err) {
    console.error('insert-image: cannot insert image files', err)
    seAlert(String(err?.message ?? err))
  }
}

/**
 * Replaces the href of an existing <image> element with a data URI, resizing
 * it to the new bitmap's natural size and firing `changed` (artifact autosave).
 * Mirrors the old TopPanel.setImageURL → svgCanvas.setImageURL flow.
 * @param {string} targetId id of the image element to update
 * @param {string} dataUrl embedded bitmap
 * @returns {Promise<void>}
 */
const replaceImageHref = (targetId, dataUrl) => new Promise((resolve) => {
  const svgCanvas = svgEditor.svgCanvas
  const elem = document.getElementById(targetId)
  if (!elem || elem.nodeName !== 'image') {
    console.error('insert-image: replace target not found', targetId)
    resolve()
    return
  }
  const img = new Image()
  img.onload = () => {
    try {
      const { BatchCommand, ChangeElementCommand } = svgCanvas.history
      const curHref = svgCanvas.getHref(elem)
      const oldW = elem.getAttribute('width')
      const oldH = elem.getAttribute('height')
      elem.setAttribute('width', img.naturalWidth)
      elem.setAttribute('height', img.naturalHeight)
      svgCanvas.setHref(elem, dataUrl)
      svgCanvas.selectorManager.requestSelector(elem).resize()
      const batch = new BatchCommand('Change Image URL')
      batch.addSubCommand(new ChangeElementCommand(elem, {
        '#href': curHref,
        width: oldW,
        height: oldH
      }))
      svgCanvas.addCommandToHistory(batch)
      svgCanvas.call('changed', [elem])
    } catch (err) {
      console.error('insert-image: cannot replace image href', err)
    }
    resolve()
  }
  img.onerror = () => {
    console.error('insert-image: replacement bitmap failed to load')
    resolve()
  }
  img.src = dataUrl
})

/**
 * Wires the dialogs to the canvas once the editor is ready.
 * @returns {void}
 */
export const initInsertImage = () => {
  const dialog = document.getElementById('se-insert-image-dialog')

  if (dialog) {
    dialog.addEventListener('change', async (e) => {
      const { trigger, mode, files, dataUrl, url } = e.detail ?? {}
      // Replace-URL flow (old promptImgURL behavior): an existing image was
      // selected and the user picked a new bitmap → swap the href.
      const targetId = dialog.getAttribute('target-image')
      const cancelDeletes = dialog.getAttribute('cancel-deletes') === 'true'
      const finishReplace = () => {
        dialog.removeAttribute('target-image')
        dialog.removeAttribute('cancel-deletes')
      }
      if (trigger === 'cancel' && targetId && cancelDeletes) {
        const target = document.getElementById(targetId)
        if (target) {
          svgEditor.svgCanvas.selectOnly([target])
          svgEditor.svgCanvas.deleteSelectedElements()
        }
      }
      if (targetId) finishReplace()
      if (trigger !== 'ok') return
      try {
        if (mode === 'file' && files?.length) {
          if (targetId) {
            const embedded = await blobToDataURL(files[0])
            await replaceImageHref(targetId, embedded)
          } else {
            await insertFilesSafely([...files])
          }
        } else if (mode === 'paste' && dataUrl) {
          if (targetId) {
            await replaceImageHref(targetId, dataUrl)
          } else {
            await insertImageFromDataUrl(dataUrl, 'clipboard')
          }
        } else if (mode === 'url' && url) {
          const { dataUrl: embedded } = await fetchImageForEmbed(url)
          if (targetId) {
            await replaceImageHref(targetId, embedded)
          } else {
            await insertImageFromDataUrl(embedded, url)
          }
        }
      } catch (err) {
        console.error('insert-image: insertion failed', err)
        seAlert(String(err?.message ?? err))
      }
    })
  } else {
    console.error('insert-image: dialog element missing')
  }

  // Ctrl/Cmd+V: system clipboard bitmap → canvas
  document.addEventListener('paste', (e) => {
    // The insert dialog manages its own paste preview when open
    if (dialog?.getAttribute('dialog') === 'open') return
    // Any open se-dialog owns the paste flow (e.g. the sticker eraser canvas)
    if (e.composedPath().some((el) => el instanceof Element && el.matches('se-sticker-dialog, se-insert-image-dialog'))) return
    const target = e.target
    const isTextInput = target instanceof HTMLElement &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (!file) continue // eslint-disable-line no-continue
        if (!isTextInput) {
          e.preventDefault()
          e.stopPropagation()
          insertFilesSafely([file])
        }
        return
      }
    }
  })

  // One transaction: captured image href + contour, never the later selection.
  const sticker = document.getElementById('se-sticker-dialog')
  sticker?.addEventListener('change', (e) => {
    const { trigger, source, sourceElement, sourceHref, png, d, naturalW, naturalH } = e.detail ?? {}
    if (trigger !== 'ok') return
    const svgCanvas = svgEditor.svgCanvas
    const elem = source === 'image' ? sourceElement : null
    const { BatchCommand, ChangeElementCommand, InsertElementCommand } = svgCanvas.history
    const changed = []
    let cutPath = null
    let hrefChanged = false
    let oldHrefAttribute
    let errorKey = 'apply_error'
    try {
      if (!(naturalW > 0 && naturalH > 0)) throw new Error('Invalid bitmap dimensions')
      let parent
      let nextSibling = null
      let transform
      if (source === 'image') {
        if (!elem || elem.nodeName !== 'image' || !document.getElementById('svgcontent')?.contains(elem) ||
            svgCanvas.getHref(elem) !== sourceHref) {
          errorKey = 'target_error'
          throw new Error('Source image was removed or changed while the dialog was open')
        }
        parent = elem.parentNode
        nextSibling = elem.nextSibling
        oldHrefAttribute = elem.getAttribute('href')
        // SVGLength.value resolves units without rewriting the original attributes.
        const width = elem.width.baseVal.value
        const height = elem.height.baseVal.value
        let tx = elem.x.baseVal.value
        let ty = elem.y.baseVal.value
        let sx = width / naturalW
        let sy = height / naturalH
        if (![tx, ty, sx, sy].every(Number.isFinite) || sx <= 0 || sy <= 0) throw new Error('Invalid image geometry')
        const aspect = elem.preserveAspectRatio.baseVal
        if (aspect.align !== 1) { // SVG_PRESERVEASPECTRATIO_NONE
          const scale = aspect.meetOrSlice === 2 ? Math.max(sx, sy) : Math.min(sx, sy)
          // A cropped slice requires a clipped contour, not an oversized boundary.
          if (d && aspect.meetOrSlice === 2 && Math.abs(sx - sy) > 1e-9) {
            errorKey = 'slice_error'
            throw new Error('A cropped slice image cannot use an untrimmed cut contour')
          }
          const align = Math.max(0, aspect.align - 2)
          tx += (width - naturalW * scale) * ((align % 3) / 2)
          ty += (height - naturalH * scale) * (Math.floor(align / 3) / 2)
          sx = sy = scale
        }
        transform = `${elem.getAttribute('transform') || ''} translate(${tx},${ty}) scale(${sx},${sy})`.trim()
      } else {
        if (!d) return
        const res = svgCanvas.getResolution()
        const scale = Math.min(res.w / naturalW, res.h / naturalH, 1)
        transform = `translate(${(res.w - naturalW * scale) / 2},${(res.h - naturalH * scale) / 2}) scale(${scale})`
        parent = svgCanvas.getDrawing().getCurrentLayer()
      }
      if (d) {
        // Detached creation preserves the image's parent and stacking order.
        cutPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        const attrs = {
          d,
          id: svgCanvas.getNextId(),
          fill: 'none',
          stroke: '#00FF00',
          'stroke-width': 1,
          'vector-effect': 'non-scaling-stroke',
          'fill-rule': 'evenodd',
          transform
        }
        for (const [name, value] of Object.entries(attrs)) cutPath.setAttribute(name, value)
      }
      const batch = new BatchCommand('Sticker transparency and cut line')
      if (elem && png) {
        if (!png.startsWith('data:image/png;base64,')) throw new Error('Invalid embedded PNG')
        hrefChanged = true
        svgCanvas.setHref(elem, png)
        // Including transform prevents rotation recentering from destroying compound matrices.
        batch.addSubCommand(new ChangeElementCommand(elem, {
          '#href': sourceHref,
          transform: elem.getAttribute('transform')
        }))
        changed.push(elem)
      }
      if (cutPath) {
        parent.insertBefore(cutPath, nextSibling)
        batch.addSubCommand(new InsertElementCommand(cutPath))
        changed.push(cutPath)
      }
      if (!batch.isEmpty()) svgCanvas.addCommandToHistory(batch)
    } catch (err) {
      cutPath?.remove()
      if (hrefChanged) {
        if (oldHrefAttribute === null) elem.removeAttribute('href')
        else elem.setAttribute('href', oldHrefAttribute)
      }
      e.preventDefault()
      sticker.reportError(errorKey, err)
      return
    }
    if (changed.length) {
      svgCanvas.call('changed', changed)
      if (!elem && cutPath) svgCanvas.selectOnly([cutPath])
      svgEditor.topPanel.updateContextPanel()
    }
  })

  // Main menu entry is added by MainMenu (tool_insert_image)
  const menuItem = document.getElementById('tool_insert_image')
  if (menuItem) {
    svgEditor.$click(menuItem, () => {
      openInsertImageDialog()
    })
  }

  // "Sticker cut line" entry is added by ext-opensave (tool_sticker_outline)
  const stickerMenu = document.getElementById('tool_sticker_outline')
  if (stickerMenu) {
    svgEditor.$click(stickerMenu, () => {
      const elem = svgEditor.svgCanvas.getSelectedElements()[0]
      openStickerDialog(elem?.nodeName === 'image' ? 'image' : 'bitmap')
    })
  }
}
